import {
  Excalidraw,
  CaptureUpdateAction,
  ExcalidrawAPIProvider,
  useExcalidrawAPI,
} from "@excalidraw/excalidraw";
import { trackEvent } from "@excalidraw/excalidraw/analytics";
import {
  CommandPalette,
  DEFAULT_CATEGORIES,
} from "@excalidraw/excalidraw/components/CommandPalette/CommandPalette";
import { ErrorDialog } from "@excalidraw/excalidraw/components/ErrorDialog";
import { OverwriteConfirmDialog } from "@excalidraw/excalidraw/components/OverwriteConfirm/OverwriteConfirm";
import { openConfirmModal } from "@excalidraw/excalidraw/components/OverwriteConfirm/OverwriteConfirmState";
import Trans from "@excalidraw/excalidraw/components/Trans";
import {
  APP_NAME,
  EVENT,
  VERSION_TIMEOUT,
  debounce,
  getVersion,
  getFrame,
  isTestEnv,
  preventUnload,
  resolvablePromise,
  isDevEnv,
} from "@excalidraw/common";
import polyfill from "@excalidraw/excalidraw/polyfill";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { loadFromBlob } from "@excalidraw/excalidraw/data/blob";
import { serializeAsJSON } from "@excalidraw/excalidraw/data/json";
import { t } from "@excalidraw/excalidraw/i18n";

import { isElementLink } from "@excalidraw/element";
import {
  restoreAppState,
  restoreElements,
} from "@excalidraw/excalidraw/data/restore";
import { newElementWith } from "@excalidraw/element";
import { isInitializedImageElement } from "@excalidraw/element";
import {
  parseLibraryTokensFromUrl,
  useHandleLibrary,
} from "@excalidraw/excalidraw/data/library";

import type { RestoredDataState } from "@excalidraw/excalidraw/data/restore";
import type {
  FileId,
  NonDeletedExcalidrawElement,
  OrderedExcalidrawElement,
} from "@excalidraw/element/types";
import type {
  AppState,
  ExcalidrawImperativeAPI,
  BinaryFiles,
  ExcalidrawInitialDataState,
  UIAppState,
  ExcalidrawProps,
} from "@excalidraw/excalidraw/types";
import type { ResolutionType } from "@excalidraw/common/utility-types";
import type { ResolvablePromise } from "@excalidraw/common/utils";

import CustomStats from "./CustomStats";
import { Provider, useAtom, useAtomValue, appJotaiStore } from "./app-jotai";
import { STORAGE_KEYS, SYNC_BROWSER_TABS_TIMEOUT } from "./app_constants";
import { AppFooter } from "./components/AppFooter";
import { AppMainMenu } from "./components/AppMainMenu";
import { AppWelcomeScreen } from "./components/AppWelcomeScreen";
import { AdminPanel } from "./components/AdminPanel";
import { AuthPage } from "./components/AuthPage";
import { Dashboard } from "./components/Dashboard";
import { TopErrorBoundary } from "./components/TopErrorBoundary";

import { authApi, screensApi } from "./data/api";
import {
  activeScreenIdAtom,
  adminPanelOpenAtom,
  authPageStateAtom,
  currentUserAtom,
  dashboardOpenAtom,
} from "./data/authState";
import { updateStaleImageStatuses } from "./data/FileManager";
import { FileStatusStore } from "./data/fileStatusStore";
import { importFromLocalStorage } from "./data/localStorage";
import {
  parseWorkspacePath,
  screenPath,
  workspacePath,
} from "./data/workspaceRoutes";

import {
  LibraryIndexedDBAdapter,
  LibraryLocalStorageMigrationAdapter,
  LocalData,
  localStorageQuotaExceededAtom,
} from "./data/LocalData";
import { isBrowserStorageStateNewer } from "./data/tabSync";
import { useHandleAppTheme } from "./useHandleAppTheme";
import { getPreferredLanguage } from "./app-language/language-detector";
import { useAppLangCode } from "./app-language/language-state";
import DebugCanvas, {
  debugRenderer,
  isVisualDebuggerEnabled,
  loadSavedDebugState,
} from "./components/DebugCanvas";

import "./index.scss";

import { AppSidebar } from "./components/AppSidebar";

polyfill();

window.EXCALIDRAW_THROTTLE_RENDER = true;

declare global {
  interface BeforeInstallPromptEventChoiceResult {
    outcome: "accepted" | "dismissed";
  }

  interface BeforeInstallPromptEvent extends Event {
    prompt(): Promise<void>;
    userChoice: Promise<BeforeInstallPromptEventChoiceResult>;
  }

  interface WindowEventMap {
    beforeinstallprompt: BeforeInstallPromptEvent;
  }
}

let pwaEvent: BeforeInstallPromptEvent | null = null;

// Adding a listener outside of the component as it may (?) need to be
// subscribed early to catch the event.
//
// Also note that it will fire only if certain heuristics are met (user has
// used the app for some time, etc.)
window.addEventListener(
  "beforeinstallprompt",
  (event: BeforeInstallPromptEvent) => {
    // prevent Chrome <= 67 from automatically showing the prompt
    event.preventDefault();
    // cache for later use
    pwaEvent = event;
  },
);

let isSelfEmbedding = false;

if (window.self !== window.top) {
  try {
    const parentUrl = new URL(document.referrer);
    const currentUrl = new URL(window.location.href);
    if (parentUrl.origin === currentUrl.origin) {
      isSelfEmbedding = true;
    }
  } catch (error) {
    // ignore
  }
}

const shareableLinkConfirmDialog = {
  title: t("overwriteConfirm.modal.shareableLink.title"),
  description: (
    <Trans
      i18nKey="overwriteConfirm.modal.shareableLink.description"
      bold={(text) => <strong>{text}</strong>}
      br={() => <br />}
    />
  ),
  actionLabel: t("overwriteConfirm.modal.shareableLink.button"),
  color: "danger",
} as const;

const initializeScene = async (opts: {
  excalidrawAPI: ExcalidrawImperativeAPI;
}): Promise<{ scene: ExcalidrawInitialDataState | null }> => {
  const externalUrlMatch = window.location.hash.match(/^#url=(.*)$/);

  const localDataState = importFromLocalStorage();

  const scene: Omit<
    RestoredDataState,
    // we're not storing files in the scene database/localStorage, and instead
    // fetch them async from a different store
    "files"
  > & {
    scrollToContent?: boolean;
  } = {
    elements: restoreElements(localDataState?.elements, null, {
      repairBindings: true,
      deleteInvisibleElements: true,
    }),
    appState: restoreAppState(localDataState?.appState, null),
  };

  if (externalUrlMatch) {
    window.history.replaceState({}, APP_NAME, window.location.origin);

    const url = externalUrlMatch[1];
    try {
      const request = await fetch(window.decodeURIComponent(url));
      const data = await loadFromBlob(await request.blob(), null, null);
      if (
        !scene.elements.length ||
        (await openConfirmModal(shareableLinkConfirmDialog))
      ) {
        return { scene: data };
      }
    } catch (error: any) {
      return {
        scene: {
          appState: {
            errorMessage: t("alerts.invalidSceneUrl"),
          },
        },
      };
    }
  }

  return { scene };
};

const ExcalidrawWrapper = () => {
  const excalidrawAPI = useExcalidrawAPI();

  const [errorMessage, setErrorMessage] = useState("");

  const { editorTheme, appTheme, setAppTheme } = useHandleAppTheme();

  const [langCode, setLangCode] = useAppLangCode();

  // account / remote screens
  // ---------------------------------------------------------------------------
  const [currentUser, setCurrentUser] = useAtom(currentUserAtom);
  const [activeScreenId, setActiveScreenId] = useAtom(activeScreenIdAtom);
  const [activeScreenCode, setActiveScreenCode] = useState<string | null>(null);
  const [activeScreenName, setActiveScreenName] = useState<string | null>(null);
  const [, setAdminPanelOpen] = useAtom(adminPanelOpenAtom);
  const [, setAuthPageState] = useAtom(authPageStateAtom);
  const [, setDashboardOpen] = useAtom(dashboardOpenAtom);

  useEffect(() => {
    if (currentUser === undefined) {
      return;
    }
    if (currentUser?.role === "ADMIN") {
      if (window.location.pathname !== "/admin") {
        window.history.replaceState({}, APP_NAME, "/admin");
      }
      setDashboardOpen(false);
      setAdminPanelOpen(true);
      return;
    }
    if (window.location.pathname !== "/admin") {
      return;
    }
    if (currentUser === null) {
      setAuthPageState({ isOpen: true, mode: "login" });
      return;
    }
    if (currentUser) {
      window.history.replaceState({}, APP_NAME, "/");
    }
  }, [currentUser, setAdminPanelOpen, setAuthPageState, setDashboardOpen]);

  useEffect(() => {
    if (currentUser === undefined) {
      return;
    }
    const mode =
      window.location.pathname === "/login"
        ? "login"
        : window.location.pathname === "/signup"
        ? "signup"
        : null;
    if (!mode) {
      return;
    }
    if (currentUser === null) {
      setAuthPageState({ isOpen: true, mode });
      return;
    }
    window.history.replaceState(
      {},
      APP_NAME,
      currentUser.role === "ADMIN"
        ? "/admin"
        : workspacePath(currentUser.publicId),
    );
  }, [currentUser, setAuthPageState]);

  useEffect(() => {
    let cancelled = false;
    authApi
      .me()
      .then((user) => {
        if (!cancelled) {
          setCurrentUser(user);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setCurrentUser(null);
        }
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openScreen = useCallback(
    async (id: string, historyMode: "push" | "replace" = "push") => {
      if (!excalidrawAPI) {
        return;
      }
      const screen = await screensApi.get(id);
      const data = (screen.data ?? {}) as {
        elements?: unknown;
        appState?: unknown;
        files?: Record<string, any>;
      };
      excalidrawAPI.updateScene({
        elements: restoreElements(data.elements as any, null, {
          repairBindings: true,
        }),
        appState: restoreAppState(data.appState as any, null),
        captureUpdate: CaptureUpdateAction.IMMEDIATELY,
      });
      if (data.files) {
        excalidrawAPI.addFiles(Object.values(data.files));
      }
      setActiveScreenId(screen.id);
      setActiveScreenCode(screen.code);
      setActiveScreenName(screen.name);
      if (currentUser?.role === "USER") {
        const nextPath = screenPath(currentUser.publicId, screen.code);
        if (window.location.pathname !== nextPath) {
          window.history[`${historyMode}State`]({}, APP_NAME, nextPath);
        }
      }
      setDashboardOpen(false);
    },
    [currentUser, excalidrawAPI, setActiveScreenId, setDashboardOpen],
  );

  // on first load (or right after a session is restored), jump straight to
  // whichever screen the URL points at instead of always showing the
  // dashboard — this is what makes a refresh/bookmark/shared link land back
  // on the same screen
  const wasLoggedIn = useRef(false);
  useEffect(() => {
    if (currentUser?.role === "USER") {
      if (!wasLoggedIn.current) {
        if (!excalidrawAPI) {
          return;
        }
        wasLoggedIn.current = true;
        const route = parseWorkspacePath(window.location.pathname);
        const belongsToCurrentUser =
          route?.userKey === currentUser.publicId ||
          route?.userKey === currentUser.id;
        if (belongsToCurrentUser && route?.screenKey) {
          openScreen(route.screenKey, "replace").catch(() => {
            window.history.replaceState(
              {},
              APP_NAME,
              workspacePath(currentUser.publicId),
            );
            setDashboardOpen(true);
          });
        } else {
          window.history.replaceState(
            {},
            APP_NAME,
            workspacePath(currentUser.publicId),
          );
          setDashboardOpen(true);
        }
      }
      return;
    }
    if (currentUser === null && wasLoggedIn.current) {
      wasLoggedIn.current = false;
      window.history.replaceState({}, APP_NAME, "/");
      setDashboardOpen(false);
      if (activeScreenId && excalidrawAPI) {
        setActiveScreenId(null);
        setActiveScreenCode(null);
        setActiveScreenName(null);
        const localDataState = importFromLocalStorage();
        excalidrawAPI.updateScene({
          elements: restoreElements(localDataState?.elements, null, {
            repairBindings: true,
          }),
          appState: restoreAppState(localDataState?.appState, null),
          captureUpdate: CaptureUpdateAction.IMMEDIATELY,
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser, excalidrawAPI]);

  useEffect(() => {
    if (currentUser?.role !== "USER") {
      return;
    }
    const handlePopState = () => {
      const route = parseWorkspacePath(window.location.pathname);
      const belongsToCurrentUser =
        route?.userKey === currentUser.publicId ||
        route?.userKey === currentUser.id;
      if (!route || !belongsToCurrentUser) {
        window.history.replaceState(
          {},
          APP_NAME,
          workspacePath(currentUser.publicId),
        );
        setDashboardOpen(true);
        return;
      }
      if (route.screenKey) {
        openScreen(route.screenKey, "replace").catch(() => {
          window.history.replaceState(
            {},
            APP_NAME,
            workspacePath(currentUser.publicId),
          );
          setDashboardOpen(true);
        });
      } else {
        setDashboardOpen(true);
      }
    };
    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, [currentUser, openScreen, setDashboardOpen]);

  const createScreen = useCallback(
    async (projectId: string | null, name: string) => {
      if (!excalidrawAPI) {
        return;
      }
      const screen = await screensApi.create({
        name,
        projectId,
        data: { elements: [], appState: {}, files: {} },
      });
      excalidrawAPI.updateScene({
        elements: [],
        appState: restoreAppState(null, null),
        captureUpdate: CaptureUpdateAction.IMMEDIATELY,
      });
      setActiveScreenId(screen.id);
      setActiveScreenCode(screen.code);
      setActiveScreenName(screen.name);
      if (currentUser?.role === "USER") {
        window.history.pushState(
          {},
          APP_NAME,
          screenPath(currentUser.publicId, screen.code),
        );
      }
      setDashboardOpen(false);
    },
    [currentUser, excalidrawAPI, setActiveScreenId, setDashboardOpen],
  );

  const importLocalScene = useCallback(async () => {
    if (!excalidrawAPI) {
      return;
    }
    const elements = excalidrawAPI.getSceneElements();
    const appState = excalidrawAPI.getAppState();
    const files = excalidrawAPI.getFiles();
    const json = JSON.parse(
      serializeAsJSON(elements, appState, files, "local"),
    );
    try {
      const screen = await screensApi.create({
        name: "My first screen",
        projectId: null,
        data: json,
      });
      setActiveScreenId(screen.id);
      setActiveScreenCode(screen.code);
      setActiveScreenName(screen.name);
      if (currentUser?.role === "USER") {
        window.history.pushState(
          {},
          APP_NAME,
          screenPath(currentUser.publicId, screen.code),
        );
      }
      setDashboardOpen(false);
    } catch (error) {
      console.error("Failed to import local scene", error);
    }
  }, [currentUser, excalidrawAPI, setActiveScreenId, setDashboardOpen]);

  const saveActiveScreenDebounced = useMemo(
    () =>
      debounce(
        (
          id: string,
          elements: readonly OrderedExcalidrawElement[],
          appState: AppState,
          files: BinaryFiles,
        ) => {
          const json = JSON.parse(
            serializeAsJSON(elements, appState, files, "local"),
          );
          screensApi
            .update(id, { data: json })
            .catch((error) => console.error("Failed to save screen", error));
        },
        800,
      ),
    [],
  );

  // initial state
  // ---------------------------------------------------------------------------

  const initialStatePromiseRef = useRef<{
    promise: ResolvablePromise<ExcalidrawInitialDataState | null>;
  }>({ promise: null! });
  if (!initialStatePromiseRef.current.promise) {
    initialStatePromiseRef.current.promise =
      resolvablePromise<ExcalidrawInitialDataState | null>();
  }

  const debugCanvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    trackEvent("load", "frame", getFrame());
    // Delayed so that the app has a time to load the latest SW
    setTimeout(() => {
      trackEvent("load", "version", getVersion());
    }, VERSION_TIMEOUT);
  }, []);

  useHandleLibrary({
    excalidrawAPI,
    adapter: LibraryIndexedDBAdapter,
    // TODO maybe remove this in several months (shipped: 24-03-11)
    migrationAdapter: LibraryLocalStorageMigrationAdapter,
  });

  const [, forceRefresh] = useState(false);

  useEffect(() => {
    if (isDevEnv()) {
      const debugState = loadSavedDebugState();

      if (debugState.enabled && !window.visualDebug) {
        window.visualDebug = {
          data: [],
        };
      } else {
        delete window.visualDebug;
      }
      forceRefresh((prev) => !prev);
    }
  }, [excalidrawAPI]);

  // ---------------------------------------------------------------------------
  // Hoisted loadImages
  // ---------------------------------------------------------------------------
  const loadImages = useCallback(
    (data: ResolutionType<typeof initializeScene>, isInitialLoad = false) => {
      if (!data.scene || !excalidrawAPI) {
        return;
      }

      const fileIds =
        data.scene.elements?.reduce((acc, element) => {
          if (isInitializedImageElement(element)) {
            return acc.concat(element.fileId);
          }
          return acc;
        }, [] as FileId[]) || [];

      if (isInitialLoad) {
        if (fileIds.length) {
          LocalData.fileStorage
            .getFiles(fileIds)
            .then(async ({ loadedFiles, erroredFiles }) => {
              if (loadedFiles.length) {
                excalidrawAPI.addFiles(loadedFiles);
              }
              updateStaleImageStatuses({
                excalidrawAPI,
                erroredFiles,
                elements: excalidrawAPI.getSceneElementsIncludingDeleted(),
              });
            });
        }
        // on fresh load, clear unused files from IDB (from previous
        // session)
        LocalData.fileStorage.clearObsoleteFiles({
          currentFileIds: fileIds,
        });
      }
    },
    [excalidrawAPI],
  );

  useEffect(() => {
    if (!excalidrawAPI) {
      return;
    }

    initializeScene({ excalidrawAPI }).then(async (data) => {
      loadImages(data, /* isInitialLoad */ true);
      initialStatePromiseRef.current.promise.resolve(data.scene);
    });

    const onHashChange = async (event: HashChangeEvent) => {
      event.preventDefault();
      const libraryUrlTokens = parseLibraryTokensFromUrl();
      if (!libraryUrlTokens) {
        excalidrawAPI.updateScene({ appState: { isLoading: true } });

        initializeScene({ excalidrawAPI }).then((data) => {
          loadImages(data);
          if (data.scene) {
            excalidrawAPI.updateScene({
              elements: restoreElements(data.scene.elements, null, {
                repairBindings: true,
              }),
              appState: restoreAppState(data.scene.appState, null),
              captureUpdate: CaptureUpdateAction.IMMEDIATELY,
            });
          }
        });
      }
    };

    const syncData = debounce(() => {
      if (isTestEnv()) {
        return;
      }
      if (!document.hidden) {
        // don't sync if local state is newer or identical to browser state
        if (isBrowserStorageStateNewer(STORAGE_KEYS.VERSION_DATA_STATE)) {
          const localDataState = importFromLocalStorage();
          setLangCode(getPreferredLanguage());
          excalidrawAPI.updateScene({
            ...localDataState,
            captureUpdate: CaptureUpdateAction.NEVER,
          });
          LibraryIndexedDBAdapter.load().then((data) => {
            if (data) {
              excalidrawAPI.updateLibrary({
                libraryItems: data.libraryItems,
              });
            }
          });
        }

        if (isBrowserStorageStateNewer(STORAGE_KEYS.VERSION_FILES)) {
          const elements = excalidrawAPI.getSceneElementsIncludingDeleted();
          const currFiles = excalidrawAPI.getFiles();
          const fileIds =
            elements?.reduce((acc, element) => {
              if (
                isInitializedImageElement(element) &&
                // only load and update images that aren't already loaded
                !currFiles[element.fileId]
              ) {
                return acc.concat(element.fileId);
              }
              return acc;
            }, [] as FileId[]) || [];
          if (fileIds.length) {
            LocalData.fileStorage
              .getFiles(fileIds)
              .then(({ loadedFiles, erroredFiles }) => {
                if (loadedFiles.length) {
                  excalidrawAPI.addFiles(loadedFiles);
                }
                updateStaleImageStatuses({
                  excalidrawAPI,
                  erroredFiles,
                  elements: excalidrawAPI.getSceneElementsIncludingDeleted(),
                });
              });
          }
        }
      }
    }, SYNC_BROWSER_TABS_TIMEOUT);

    const onUnload = () => {
      LocalData.flushSave();
    };

    const visibilityChange = (event: FocusEvent | Event) => {
      if (event.type === EVENT.BLUR || document.hidden) {
        LocalData.flushSave();
      }
      if (
        event.type === EVENT.VISIBILITY_CHANGE ||
        event.type === EVENT.FOCUS
      ) {
        syncData();
      }
    };

    window.addEventListener(EVENT.HASHCHANGE, onHashChange, false);
    window.addEventListener(EVENT.UNLOAD, onUnload, false);
    window.addEventListener(EVENT.BLUR, visibilityChange, false);
    document.addEventListener(EVENT.VISIBILITY_CHANGE, visibilityChange, false);
    window.addEventListener(EVENT.FOCUS, visibilityChange, false);
    return () => {
      window.removeEventListener(EVENT.HASHCHANGE, onHashChange, false);
      window.removeEventListener(EVENT.UNLOAD, onUnload, false);
      window.removeEventListener(EVENT.BLUR, visibilityChange, false);
      window.removeEventListener(EVENT.FOCUS, visibilityChange, false);
      document.removeEventListener(
        EVENT.VISIBILITY_CHANGE,
        visibilityChange,
        false,
      );
    };
  }, [excalidrawAPI, setLangCode, loadImages]);

  useEffect(() => {
    const unloadHandler = (event: BeforeUnloadEvent) => {
      LocalData.flushSave();

      if (
        excalidrawAPI &&
        LocalData.fileStorage.shouldPreventUnload(
          excalidrawAPI.getSceneElements(),
        )
      ) {
        if (import.meta.env.VITE_APP_DISABLE_PREVENT_UNLOAD !== "true") {
          preventUnload(event);
        } else {
          console.warn(
            "preventing unload disabled (VITE_APP_DISABLE_PREVENT_UNLOAD)",
          );
        }
      }
    };
    window.addEventListener(EVENT.BEFORE_UNLOAD, unloadHandler);
    return () => {
      window.removeEventListener(EVENT.BEFORE_UNLOAD, unloadHandler);
    };
  }, [excalidrawAPI]);

  const onChange = (
    elements: readonly OrderedExcalidrawElement[],
    appState: AppState,
    files: BinaryFiles,
  ) => {
    if (activeScreenId) {
      saveActiveScreenDebounced(activeScreenId, elements, appState, files);
    } else if (!LocalData.isSavePaused()) {
      // this check is redundant, but since this is a hot path, it's best
      // not to evaludate the nested expression every time
      LocalData.save(elements, appState, files, () => {
        if (excalidrawAPI) {
          let didChange = false;

          const elements = excalidrawAPI
            .getSceneElementsIncludingDeleted()
            .map((element) => {
              if (
                LocalData.fileStorage.shouldUpdateImageElementStatus(element)
              ) {
                const newElement = newElementWith(element, { status: "saved" });
                if (newElement !== element) {
                  didChange = true;
                }
                return newElement;
              }
              return element;
            });

          if (didChange) {
            excalidrawAPI.updateScene({
              elements,
              captureUpdate: CaptureUpdateAction.NEVER,
            });
          }
        }
      });
    }

    // Render the debug scene if the debug canvas is available
    if (debugCanvasRef.current && excalidrawAPI) {
      debugRenderer(
        debugCanvasRef.current,
        appState,
        elements,
        window.devicePixelRatio,
      );
    }
  };

  const renderCustomStats = (
    elements: readonly NonDeletedExcalidrawElement[],
    appState: UIAppState,
  ) => {
    return (
      <CustomStats
        setToast={(message) => excalidrawAPI!.setToast({ message })}
        appState={appState}
        elements={elements}
      />
    );
  };

  const renderTopLeftUI = () => {
    if (!currentUser || !activeScreenName) {
      return null;
    }
    return (
      <div className="ScreenTitleBadge" title={activeScreenName}>
        {activeScreenName}
      </div>
    );
  };

  const localStorageQuotaExceeded = useAtomValue(localStorageQuotaExceededAtom);

  // ---------------------------------------------------------------------------
  // onExport — intercepts file save to wait for pending image loads
  // ---------------------------------------------------------------------------
  const onExport: Required<ExcalidrawProps>["onExport"] = useCallback(
    async function* () {
      let snapshot = FileStatusStore.getSnapshot();
      const { pending, total } = FileStatusStore.getPendingCount(
        snapshot.value,
      );
      if (pending === 0) {
        return;
      }

      // Yield initial progress
      yield {
        type: "progress",
        progress: (total - pending) / total,
        message: `Loading images (${total - pending}/${total})...`,
      };

      // Wait for all pending images to finish
      while (true) {
        snapshot = await FileStatusStore.pull(snapshot.version);
        const { pending: nowPending, total: nowTotal } =
          FileStatusStore.getPendingCount(snapshot.value);

        yield {
          type: "progress",
          progress: (nowTotal - nowPending) / nowTotal,
          message: `Loading images (${nowTotal - nowPending}/${nowTotal})...`,
        };

        if (nowPending === 0) {
          await new Promise((r) => setTimeout(r, 500));
          yield {
            type: "progress",
            message: `Preparing export...`,
          };
          return;
        }
      }
    },
    [],
  );

  // const onExport = () => {
  //   return new Promise((r) => setTimeout(r, 2500));
  //   // console.log("onExport");
  // };

  // browsers generally prevent infinite self-embedding, there are
  // cases where it still happens, and while we disallow self-embedding
  // by not whitelisting our own origin, this serves as an additional guard
  if (isSelfEmbedding) {
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          textAlign: "center",
          height: "100%",
        }}
      >
        <h1>I'm not a pretzel!</h1>
      </div>
    );
  }

  return (
    <div style={{ height: "100%" }} className="excalidraw-app">
      <Excalidraw
        onChange={onChange}
        onExport={onExport}
        initialData={initialStatePromiseRef.current.promise}
        aiEnabled={false}
        UIOptions={{
          canvasActions: {
            toggleTheme: true,
          },
        }}
        langCode={langCode}
        renderCustomStats={renderCustomStats}
        renderTopLeftUI={renderTopLeftUI}
        detectScroll={false}
        handleKeyboardGlobally={true}
        autoFocus={true}
        theme={editorTheme}
        onThemeChange={setAppTheme}
        onLinkOpen={(element, event) => {
          if (element.link && isElementLink(element.link)) {
            event.preventDefault();
            excalidrawAPI?.setViewport({
              target: element.link,
              fit: "scale-down",
              animation: true,
            });
          }
        }}
      >
        <AppMainMenu theme={appTheme} />
        <AppWelcomeScreen />
        <OverwriteConfirmDialog>
          <OverwriteConfirmDialog.Actions.ExportToImage />
          <OverwriteConfirmDialog.Actions.SaveToDisk />
        </OverwriteConfirmDialog>
        <AppFooter onChange={() => excalidrawAPI?.refresh()} />

        {localStorageQuotaExceeded && (
          <div className="alert alert--danger">
            {t("alerts.localStorageQuotaExceeded")}
          </div>
        )}

        <AppSidebar />

        <AuthPage />
        {currentUser?.role === "ADMIN" && (
          <AdminPanel
            currentUserId={currentUser.id}
            onLogout={() => {
              authApi.logout().finally(() => {
                setAdminPanelOpen(false);
                setCurrentUser(null);
              });
            }}
          />
        )}
        {currentUser?.role === "USER" && (
          <Dashboard
            currentUserEmail={currentUser.email}
            onOpenScreen={openScreen}
            onCreateScreen={createScreen}
            onClose={
              activeScreenId && activeScreenCode
                ? () => {
                    setDashboardOpen(false);
                    window.history.pushState(
                      {},
                      APP_NAME,
                      screenPath(currentUser.publicId, activeScreenCode),
                    );
                  }
                : undefined
            }
            onLogout={() => {
              authApi.logout().finally(() => setCurrentUser(null));
            }}
            hasLocalScene={
              !activeScreenId &&
              (excalidrawAPI?.getSceneElements().length ?? 0) > 0
            }
            onImportLocalScene={importLocalScene}
          />
        )}

        {errorMessage && (
          <ErrorDialog onClose={() => setErrorMessage("")}>
            {errorMessage}
          </ErrorDialog>
        )}

        <CommandPalette
          customCommandPaletteItems={[
            {
              label: t("labels.installPWA"),
              category: DEFAULT_CATEGORIES.app,
              predicate: () => !!pwaEvent,
              perform: () => {
                if (pwaEvent) {
                  pwaEvent.prompt();
                  pwaEvent.userChoice.then(() => {
                    // event cannot be reused, but we'll hopefully
                    // grab new one as the event should be fired again
                    pwaEvent = null;
                  });
                }
              },
            },
          ]}
        />
        {isVisualDebuggerEnabled() && excalidrawAPI && (
          <DebugCanvas
            appState={excalidrawAPI.getAppState()}
            scale={window.devicePixelRatio}
            ref={debugCanvasRef}
          />
        )}
      </Excalidraw>
    </div>
  );
};

const ExcalidrawApp = () => {
  return (
    <TopErrorBoundary>
      <Provider store={appJotaiStore}>
        <ExcalidrawAPIProvider>
          <ExcalidrawWrapper />
        </ExcalidrawAPIProvider>
      </Provider>
    </TopErrorBoundary>
  );
};

export default ExcalidrawApp;
